import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";
import { Mail, Phone } from "lucide-react";

export const Route = createFileRoute("/contact")({
  head: () => ({
    meta: [
      { title: "Contact, HOWDEN" },
      {
        name: "description",
        content:
          "Speak with HOWDEN about lab-grown diamond jewelry and bespoke commissions.",
      },
      { property: "og:title", content: "Contact, HOWDEN" },
      {
        property: "og:description",
        content:
          "Speak with HOWDEN about lab-grown diamond jewelry and bespoke commissions.",
      },
      { property: "og:url", content: "https://howdendiamonds.com/contact" },
    ],
    links: [{ rel: "canonical", href: "https://howdendiamonds.com/contact" }],
  }),
  component: ContactPage,
});

function ContactPage() {
  const [sending, setSending] = useState(false);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setSending(true);
    setTimeout(() => {
      setSending(false);
      (e.target as HTMLFormElement).reset();
      toast.success("Message received", {
        description: "We will respond within one business day.",
      });
    }, 800);
  };

  return (
    <div className="mx-auto max-w-7xl px-6 py-20 lg:px-12 lg:py-32">
      <div className="grid gap-20 lg:grid-cols-2">
        <div>
          <p className="text-xs uppercase tracking-luxe text-muted-foreground">
            Correspondence
          </p>
          <h1 className="mt-6 font-serif text-5xl font-light leading-[1.05] lg:text-6xl">
            Tell us what you seek.
          </h1>
          <p className="mt-8 max-w-md text-base leading-relaxed text-muted-foreground">
            Custom shapes, specific carat ranges, particular grading preferences ,
            our team will source to your requirements. Private viewings by
            appointment.
          </p>

          <ul className="mt-16 space-y-6">
            <li className="flex items-start gap-4">
              <Mail className="mt-1 h-4 w-4 text-accent-foreground/70" strokeWidth={1.25} />
              <div>
                <p className="text-xs uppercase tracking-luxe text-muted-foreground">
                  Email
                </p>
                <p className="mt-1 font-serif text-xl">hello@howden.diamonds</p>
              </div>
            </li>
            <li className="flex items-start gap-4">
              <Phone className="mt-1 h-4 w-4 text-accent-foreground/70" strokeWidth={1.25} />
              <div>
                <p className="text-xs uppercase tracking-luxe text-muted-foreground">
                  By appointment
                </p>
                <p className="mt-1 font-serif text-xl">+1 (555) 018-8000</p>
              </div>
            </li>
          </ul>
        </div>

        <form onSubmit={handleSubmit} className="space-y-8 lg:pt-12">
          <Field label="Name" name="name" required />
          <Field label="Email" name="email" type="email" required />
          <Field label="Subject" name="subject" />
          <div>
            <label className="text-xs uppercase tracking-luxe text-muted-foreground">
              Message
            </label>
            <textarea
              name="message"
              required
              rows={6}
              className="mt-3 w-full border-b border-hairline bg-transparent py-3 text-base outline-none transition-colors focus:border-foreground"
            />
          </div>
          <button
            type="submit"
            disabled={sending}
            className="h-12 w-full bg-foreground text-xs uppercase tracking-luxe text-background transition-opacity hover:opacity-90 disabled:opacity-50 sm:w-auto sm:px-12"
          >
            {sending ? "Sending…" : "Send message"}
          </button>
        </form>
      </div>
    </div>
  );
}

function Field({
  label,
  name,
  type = "text",
  required,
}: {
  label: string;
  name: string;
  type?: string;
  required?: boolean;
}) {
  return (
    <div>
      <label className="text-xs uppercase tracking-luxe text-muted-foreground">
        {label}
        {required && " *"}
      </label>
      <input
        type={type}
        name={name}
        required={required}
        className="mt-3 w-full border-b border-hairline bg-transparent py-3 text-base outline-none transition-colors focus:border-foreground"
      />
    </div>
  );
}
